import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ResumeUpload from "@/components/ResumeUpload";
import MathCaptcha from "@/components/MathCaptcha";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, MessageCircle, Send, Loader2, RotateCcw, CheckCircle2, AlertCircle } from "lucide-react";

const WA_LINK = (score, job) =>
  `https://wa.me/917483500550?text=${encodeURIComponent(
    `Hi Ravemora! I just scored ${score}/100 on the ${job?.title || ""} role using your AI screener. I'd like to book a career counselling session.`
  )}`;

export default function AIScreeningZone({ selectedJob }) {
  const [resumeText, setResumeText] = useState("");
  const [fileMeta, setFileMeta] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cover_note: "" });
  const [captchaOk, setCaptchaOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const applyFormRef = useRef(null);

  const runScore = async (text) => {
    if (!selectedJob) { toast.error("Pick a role on the left first."); return; }
    if (!text || text.trim().length < 40) { toast.error("Resume text is too short."); return; }
    setScoring(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/util/score-resume", { job_id: selectedJob.job_id, resume_text: text });
      setAiResult(data);
      setTimeout(() => applyFormRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 250);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI scoring failed. Please try again.");
    } finally {
      setScoring(false);
    }
  };

  const handleResumeChange = (text) => {
    setResumeText(text);
    if (text && text.trim().length >= 40 && selectedJob) runScore(text);
  };

  const reset = () => {
    setResumeText(""); setFileMeta(null); setAiResult(null);
    setForm({ full_name: "", email: "", phone: "", cover_note: "" });
    setCaptchaOk(false); setSubmitted(false);
  };

  const submitApplication = async () => {
    if (!selectedJob || !aiResult) return;
    if (!form.full_name || !form.email || !form.phone) { toast.error("Please share your name, email and phone."); return; }
    if (!captchaOk) { toast.error("Please solve the captcha."); return; }
    setSubmitting(true);
    try {
      await api.post("/applications", {
        job_id: selectedJob.job_id,
        ...form,
        resume_text: resumeText,
        resume_filename: fileMeta?.filename,
        resume_file_b64: fileMeta?.file_b64,
        resume_content_type: fileMeta?.content_type,
        ai_score: aiResult.score,
        ai_strengths: aiResult.strengths,
        ai_gaps: aiResult.gaps,
        ai_summary: aiResult.summary,
      });
      setSubmitted(true);
      toast.success("Application submitted — we've been notified with your AI match score.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Score visualisation on WHITE bg so it reads clearly inside the navy panel.
  const scoreBg = (s) =>
    s >= 80 ? "bg-emerald-50 border-emerald-500"
    : s >= 60 ? "bg-white border-navy"
    : s >= 40 ? "bg-[#FFF6D6] border-[#F5B700]"
    : "bg-red-50 border-red-500";
  const scoreText = (s) =>
    s >= 80 ? "text-emerald-700"
    : s >= 60 ? "text-navy"
    : s >= 40 ? "text-[#B8860B]"
    : "text-red-700";

  return (
    <div className="lg:sticky lg:top-24 min-w-0" data-testid="ai-screening-zone">
      <div className="bg-navy text-offwhite border border-gold/30 grain overflow-hidden">
        {/* Header */}
        <div className="p-6 md:p-7 border-b border-offwhite/10">
          <div className="inline-flex items-center gap-2 label-eyebrow border border-gold/40 px-3 py-1.5">
            <Sparkles size={12} /> AI Screening Zone
          </div>
          <h2 className="serif-display text-2xl mt-4 leading-tight">
            Drop your resume.<br />
            <span className="text-gold">Know instantly if you fit.</span>
          </h2>
          {selectedJob ? (
            <p className="mt-3 text-sm text-offwhite/70 break-words">
              Screening against <span className="text-offwhite font-semibold">{selectedJob.title}</span>
              {selectedJob.location ? <> · {selectedJob.location}</> : null}
            </p>
          ) : (
            <p className="mt-3 text-sm text-offwhite/70">Pick a role on the left, then upload your resume.</p>
          )}
        </div>

        {/* Body */}
        <div className="p-6 md:p-7 space-y-5">
          {/* Upload */}
          <div>
            <div className="label-eyebrow mb-3">Step 1 · Upload Resume</div>
            <div className="bg-white/[0.04] border border-offwhite/15 p-3">
              <ResumeUpload
                value={resumeText}
                onChange={handleResumeChange}
                onFileMeta={setFileMeta}
                testid="ai-zone-resume"
              />
            </div>
          </div>

          {/* Scoring */}
          {scoring && (
            <div className="flex items-center gap-3 text-sm text-offwhite/85 border border-offwhite/15 bg-white/[0.03] p-4" data-testid="ai-zone-scoring">
              <Loader2 className="animate-spin text-gold" size={18} />
              Screening your resume against the job description…
            </div>
          )}

          {/* Score reveal — WHITE card that pops on the navy panel */}
          {aiResult && !scoring && (
            <div
              className={`border-l-4 border ${scoreBg(aiResult.score)} p-5 min-w-0`}
              data-testid="ai-zone-score"
            >
              <div className={`flex items-baseline gap-2 ${scoreText(aiResult.score)} min-w-0`}>
                <div className="serif-display text-5xl leading-none font-semibold">{aiResult.score}</div>
                <div className="text-sm opacity-70">/ 100</div>
                <div className="ml-auto text-[10px] uppercase tracking-[0.24em] font-bold opacity-80 whitespace-nowrap">
                  Match Score
                </div>
              </div>

              {aiResult.summary ? (
                <p className="mt-3 text-sm leading-relaxed text-navy/85 break-words">
                  {aiResult.summary}
                </p>
              ) : null}

              {(aiResult.strengths?.length || aiResult.gaps?.length) ? (
                <div className="grid grid-cols-1 gap-3 mt-4 text-xs">
                  {aiResult.strengths?.length ? (
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700">
                        <CheckCircle2 size={11} /> Strengths
                      </div>
                      <ul className="mt-1.5 space-y-1 text-navy/85 break-words list-none">
                        {aiResult.strengths.slice(0, 4).map((s, i) => (
                          <li key={i} className="flex gap-1.5"><span className="text-emerald-600">◆</span><span className="min-w-0 break-words">{s}</span></li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {aiResult.gaps?.length ? (
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-red-600">
                        <AlertCircle size={11} /> Gaps
                      </div>
                      <ul className="mt-1.5 space-y-1 text-navy/85 break-words list-none">
                        {aiResult.gaps.slice(0, 4).map((g, i) => (
                          <li key={i} className="flex gap-1.5"><span className="text-red-500">◆</span><span className="min-w-0 break-words">{g}</span></li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}

          {/* WhatsApp CTA + apply form */}
          {aiResult && !submitted && (
            <div ref={applyFormRef} className="space-y-4">
              <a
                href={WA_LINK(aiResult.score, selectedJob)}
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white uppercase tracking-[0.12em] text-[11px] font-bold py-3 px-3 transition-colors text-center"
                data-testid="ai-zone-whatsapp"
              >
                <MessageCircle size={14} />
                Book a Career Counselling Session
              </a>

              <div className="border-t border-offwhite/10 pt-4">
                <div className="label-eyebrow mb-3">Step 2 · Submit Application</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <Label className="text-offwhite/80 text-xs">Full name</Label>
                    <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-white/[0.06] border-offwhite/20 text-offwhite" data-testid="ai-zone-name" />
                  </div>
                  <div className="min-w-0">
                    <Label className="text-offwhite/80 text-xs">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/[0.06] border-offwhite/20 text-offwhite" data-testid="ai-zone-email" />
                  </div>
                  <div className="sm:col-span-2 min-w-0">
                    <Label className="text-offwhite/80 text-xs">Phone (WhatsApp preferred)</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/[0.06] border-offwhite/20 text-offwhite" data-testid="ai-zone-phone" />
                  </div>
                  <div className="sm:col-span-2 min-w-0">
                    <Label className="text-offwhite/80 text-xs">Cover note (optional)</Label>
                    <Textarea rows={2} value={form.cover_note} onChange={(e) => setForm({ ...form, cover_note: e.target.value })} className="bg-white/[0.06] border-offwhite/20 text-offwhite" data-testid="ai-zone-cover" />
                  </div>
                </div>
                <div className="mt-3 bg-white/[0.04] p-3 border border-offwhite/10">
                  <MathCaptcha onValid={setCaptchaOk} testidPrefix="ai-zone-captcha" />
                </div>
                <button
                  type="button"
                  onClick={submitApplication}
                  disabled={submitting || !captchaOk}
                  className="mt-4 w-full flex items-center justify-center gap-2 btn-cream disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ai-zone-submit"
                >
                  {submitting ? (<><Loader2 className="animate-spin" size={14} /> Submitting…</>) : (<><Send size={14} /> Submit application</>)}
                </button>
              </div>
            </div>
          )}

          {submitted && (
            <div className="border border-emerald-500/40 bg-emerald-500/10 p-5 space-y-3 min-w-0" data-testid="ai-zone-submitted">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 size={18} />
                <div className="serif-display text-xl">Application received.</div>
              </div>
              <p className="text-sm text-offwhite/85 leading-relaxed break-words">
                Your resume and your <strong>{aiResult?.score}/100</strong> match score have been sent to our recruiters. If you're shortlisted, we'll reach out within 3 working days.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <a href={WA_LINK(aiResult?.score, selectedJob)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-semibold">
                  <MessageCircle size={14} /> Book a Career Counselling Session
                </a>
                <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 text-offwhite/60 hover:text-offwhite text-sm" data-testid="ai-zone-reset">
                  <RotateCcw size={14} /> Screen for another role
                </button>
              </div>
            </div>
          )}

          {!aiResult && !scoring && !submitted && resumeText.trim().length < 40 && (
            <div className="text-xs text-offwhite/50 leading-relaxed">
              Your resume never leaves your browser until you hit submit. The AI screener works instantly and privately.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
